import { getSupabaseBrowserClient } from '../lib/supabase'
import { toCustomerSafeRpcError } from './customer-safe-operation-error'

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse

function client() {
  return getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
}

async function invokeRaw(functionName: string, args: Record<string, unknown>) {
  const { data, error } = await client().rpc(functionName, args)
  if (error) {
    console.error(`Supabase RPC ${functionName} failed`, error)
    throw new Error(error.message)
  }
  return data
}

async function invokeOperation(functionName: string, args: Record<string, unknown>) {
  try {
    return await invokeRaw(functionName, args)
  } catch (error) {
    if (error instanceof Error) throw toCustomerSafeRpcError(functionName, { message: error.message })
    throw toCustomerSafeRpcError(functionName, { message: '' })
  }
}

export async function getApprovalTask(requirementId: string) {
  return invokeOperation('get_r1_approval_task', { p_requirement_id: requirementId })
}

export async function getApprovalCoordination(requirementId: string) {
  return invokeRaw('get_r1_approval_coordination', { p_requirement_id: requirementId })
}

export async function reassignApprovalRequirement(
  requirementId: string,
  expectedAssignmentRevision: number,
  membershipId: string,
  reason: string,
) {
  return invokeOperation('reassign_r1_approval_requirement', {
    p_requirement_id: requirementId,
    p_expected_assignment_revision: expectedAssignmentRevision,
    p_membership_id: membershipId,
    p_reason: reason,
  })
}

export async function recallApprovalRequirement(
  requirementId: string,
  expectedAssignmentRevision: number,
  reason: string,
) {
  return invokeOperation('recall_r1_approval_requirement', {
    p_requirement_id: requirementId,
    p_expected_assignment_revision: expectedAssignmentRevision,
    p_reason: reason,
  })
}

export type ApprovalConditionInput = {
  title: string
  description: string
  completionCriteria: string
  ownerMembershipId?: string
  dueDate?: string
  triggerDescription?: string
  consequence?: string
}

export async function recordApprovalDecision(
  requirementId: string,
  decision: 'approved' | 'approved_with_conditions' | 'rejected',
  rationale: string,
  validFrom: string | null,
  validTo: string | null,
  conditions: ApprovalConditionInput[],
) {
  return invokeOperation('record_phase3_approval_decision', {
    p_approval_requirement_id: requirementId,
    p_decision: decision,
    p_rationale: rationale,
    p_valid_from: validFrom,
    p_valid_to: validTo,
    p_conditions: conditions,
  })
}
